import { Box, InputLabel, TextField as MuiTextField } from '@mui/material';
import { SxProps, Theme } from '@mui/material';
import { ComponentPropsWithoutRef, forwardRef, useCallback, useId } from 'react';

/**
 * Attributes that ask a password manager to leave a field alone. `autoComplete` is the standard
 * one and the one they most often ignore; the rest are each vendor's documented opt-out.
 *
 * Managers also guess from the field's `name` and `id`, so a field called something like
 * `userName` may still be treated as a login even with these set - rename it as well.
 */
export const NO_AUTOFILL_ATTRS = {
    autoComplete: 'off',
    'data-lpignore': 'true', // LastPass
    'data-1p-ignore': '', // 1Password
    'data-bwignore': 'true', // Bitwarden
    'data-form-type': 'other' // Dashlane
};

export type TextFieldProps = ComponentPropsWithoutRef<typeof MuiTextField> & {
    maxLength?: number;
    /** Ask password managers not to autofill this field. See `NO_AUTOFILL_ATTRS`. */
    noAutofill?: boolean;
    noMargin?: boolean;
    readOnly?: boolean;
    labelSx?: SxProps<Theme>;
};

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>((allProps, ref) => {
    const {
        size = 'small',
        maxLength,
        noAutofill,
        name,
        label,
        noMargin,
        readOnly,
        value,
        labelSx,
        required,
        inputRef: inputRefProp,
        /*
         * ★ PULLED OUT SO THEY REACH THE <input>, NOT THE WRAPPER (ROBO-324 class).
         *
         * `aria-label` is not a MUI TextField prop, so riding `{...props}` it was forwarded to the
         * root FormControl — naming a <div> while the <input> stayed anonymous. The control looked
         * labelled in review and in the DOM, and was unnamed to assistive tech and to any query by
         * accessible name. Found when an E2E spec could not locate the Order Questions "Possible
         * answer N" fields, which pass `aria-label` and no visible `label`.
         *
         * The routes to the real input are `inputProps` / `slotProps.htmlInput`; these are merged
         * there below. Same failure shape as SelectField's `aria-labelledby` (ROBO-324): a prop that
         * reads as correct and never reaches the element it names.
         *
         * `aria-describedby` is deliberately NOT hoisted — MUI owns it for helper-text wiring, and
         * overriding it would break the error/helper association these fields rely on.
         */
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledBy,
        ...props
    } = allProps;

    const inputId = useId();

    /** MUI TextField inputRef + forwardRef ref must both attach; the latter used to overwrite the former. */
    const mergedInputRef = useCallback(
        (node: HTMLInputElement | null) => {
            if (typeof ref === 'function') {
                ref(node);
            } else if (ref) {
                ref.current = node;
            }
            if (typeof inputRefProp === 'function') {
                inputRefProp(node);
            } else if (inputRefProp) {
                inputRefProp.current = node;
            }
        },
        [ref, inputRefProp]
    );
    const shouldNormalizeValue = 'value' in allProps;
    const normalizedValue = shouldNormalizeValue ? (value ?? '') : undefined;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                width: '100%',
                mb: noMargin ? 0 : 2
            }}
        >
            {label && (
                <InputLabel
                    shrink={false}
                    id={`${inputId}-label`}
                    htmlFor={inputId}
                    required={required}
                    sx={{
                        fontSize: 12,
                        color: 'text.secondary',
                        ...labelSx
                    }}
                >
                    {label}
                </InputLabel>
            )}
            <MuiTextField
                {...props}
                id={inputId}
                name={name}
                size={size}
                required={required}
                inputRef={mergedInputRef}
                label={null}
                fullWidth
                {...(shouldNormalizeValue && { value: normalizedValue })}
                margin="none"
                sx={{
                    ...props.sx,
                    ...(readOnly && {
                        '& .MuiInputBase-root': {
                            backgroundColor: 'var(--background-content, #fff)',
                            cursor: 'default'
                        },
                        '& .MuiInputBase-input': {
                            color: 'text.secondary'
                        }
                    })
                }}
                InputProps={props.InputProps}
                inputProps={{
                    ...(ariaLabel != null ? { 'aria-label': ariaLabel } : {}),
                    ...(ariaLabelledBy != null ? { 'aria-labelledby': ariaLabelledBy } : {}),
                    ...props.inputProps,
                    ...(maxLength != null ? { maxLength } : {})
                }}
                slotProps={{
                    ...props.slotProps,
                    input: {
                        ...props.InputProps,
                        ...props.slotProps?.input,
                        readOnly
                    },
                    htmlInput: {
                        /*
                         * First, so an explicit `inputProps`/`slotProps.htmlInput` from the caller
                         * still wins — this is a fallback for the convenient spelling, not an
                         * override of the precise one.
                         */
                        ...(noAutofill ? NO_AUTOFILL_ATTRS : {}),
                        ...(ariaLabel != null ? { 'aria-label': ariaLabel } : {}),
                        ...(ariaLabelledBy != null ? { 'aria-labelledby': ariaLabelledBy } : {}),
                        ...props.inputProps,
                        ...props.slotProps?.htmlInput,
                        ...(maxLength != null ? { maxLength } : {})
                    }
                }}
            />
        </Box>
    );
});

TextField.displayName = 'TextField';
